
  import { mapGetters } from 'vuex'
  import { customSetCookie, getCookie } from '~/utils/deviceInfo'
  export default {
    computed: {
      ...mapGetters({
        liveChat: 'cms/liveChat'
      })
    },
    watch: {
      liveChat(val) {
        if (val?.is_visible && val?.chat_config?.crisp_website_id) {
          this.loadCrispChat()
        }
      }
    },
    mounted() {
      // Already loaded (e.g. cached in store)
      if (this.liveChat?.is_visible && this.liveChat?.chat_config?.crisp_website_id) {
        this.loadCrispChat()
      }
      // This component only exists in layouts that render it (theme-2 /
      // theme-3). It's unmounted entirely when navigating to routes on the
      // 'empty' layout (e.g. /play-game) and remounted on the way back —
      // loadCrispChat() no-ops on that remount (see __crispChatLoaded
      // guard below), so re-sync visibility here on every mount.
      this.updateChatVisibility()
    },
    beforeDestroy() {
      // Runs when navigating to a layout that doesn't render this
      // component (currently /play-game) — hide the widget before it
      // disappears along with the rest of the layout.
      if (window.$crisp) {
        window.$crisp.push(['do', 'chat:hide'])
      }
    },
    methods: {
      updateChatVisibility() {
        if (!window.$crisp) {
          return
        }
        if (window.location.pathname.includes('/play-game')) {
          window.$crisp.push(['do', 'chat:hide'])
        } else {
          window.$crisp.push(['do', 'chat:show'])
        }
      },
      loadCrispChat() {
        // mounted() and the liveChat watcher can both call this (the CMS
        // getter returns a new object ref on every refetch). Guard on a
        // dedicated flag rather than CRISP_WEBSITE_ID, and only set it
        // once everything below has actually succeeded — see the
        // try/catch note further down for why.
        if (window.__crispChatLoaded) {
          return
        }

        try {
          // message:show has no "undo" in the Crisp SDK — once shown,
          // it's cached in the visitor's local Crisp session and keeps
          // reappearing on reload even after the CMS flag is switched
          // off or the locale (and so the text) changes. Store which
          // locale we last showed it for, so we can tell a stale
          // bubble apart from one that still matches the current state.
          const locale = this.handleCookie('wl_locale')
          const welcomeText = this.liveChat?.chat_config?.welcome_text?.[locale]
          const showWelcome = this.isMobileOnly()
            ? this.liveChat?.chat_config?.welcome_text_mobile_status
            : this.liveChat?.chat_config?.welcome_text_desktop_status
          const shouldShowWelcome = showWelcome && welcomeText

          const welcomeShownCookie = getCookie('crisp_welcome_shown')
          const welcomeAlreadyShown = shouldShowWelcome && welcomeShownCookie === locale

          // Logged-in players get identified by player_id. Anonymous
          // visitors get a random id, persisted for a year in a cookie
          // so it stays the same across visits instead of changing
          // every load.
          const playerCookie = getCookie('player')
          let playerId = null
          if (playerCookie) {
            try {
              playerId = JSON.parse(decodeURIComponent(playerCookie))?.player_id
            } catch (e) {
              playerId = null
            }
          }
          const visitorCookie = getCookie('visitor_id')
          const randomVisitorId = String(Math.floor(Math.random() * 100000)).padStart(5, '0')
          if (!visitorCookie) {
            customSetCookie('visitor_id', randomVisitorId, 365)
          }
          const visitorId = 'Visitor-' + (visitorCookie || randomVisitorId)

          // Official Crisp init: queue commands on window.$crisp before
          // the widget script loads; Crisp flushes the queue once it's
          // ready.
          window.$crisp = []
          window.CRISP_WEBSITE_ID = this.liveChat?.chat_config?.crisp_website_id

          // The CMS flag just switched from "show" to "hide", or the
          // locale changed since the welcome bubble was shown — reset
          // the local Crisp session once to clear the stale welcome
          // bubble. Must run before the 'set' commands below, since
          // session:reset wipes any session data queued before it.
          if (welcomeShownCookie && !welcomeAlreadyShown) {
            window.$crisp.push(['do', 'session:reset'])
            customSetCookie('crisp_welcome_shown', '', -1)
          }

          window.$crisp.push(['set', 'session:segments', [[window.location.hostname]]])
          window.$crisp.push([
            'set',
            'user:nickname',
            [
              // Prefix with hostname so agents can tell which site
              // the chat came from; fall back to visitorId when
              // logged out.
              `[${window.location.hostname?.replace('.com', '')}] ${playerId || visitorId}`
            ]
          ])
          window.$crisp.push([
            'set',
            'session:data',
            [
              [
                ['landing_slug', window.location.hostname],
                ['landing_tag', window.location.hostname?.replace('.com', '')],
                ['landing_name', window.location.hostname?.replace('.com', '')],
                [
                  'source_url',
                  window.document.referrer || window.location.origin + window.location.pathname
                ],
                // Only tag the session with a customer id when
                // the visitor is actually logged in.
                ...(playerId ? [['customer', playerId]] : [])
              ]
            ]
          ])
          this.updateChatVisibility()

          // Show a welcome message to the visitor when the chat widget
          // opens.
          if (shouldShowWelcome && !welcomeAlreadyShown) {
            window.$crisp.push(['do', 'message:show', ['text', welcomeText]])
            customSetCookie('crisp_welcome_shown', locale, 365)
          }

          // Injects the Crisp widget script itself (standard Crisp
          // snippet).
          ;(function () {
            const d = document
            const s = d.createElement('script')
            s.src = 'https://client.crisp.chat/l.js'
            s.async = 1
            d.getElementsByTagName('head')[0].appendChild(s)
          })()

          // Only mark as loaded once everything above has succeeded —
          // if something throws mid-setup, this stays false so the
          // next mounted()/watcher call can retry instead of the
          // widget being silently and permanently stuck unloaded.
          window.__crispChatLoaded = true
        } catch (error) {
          // console.error('Failed to load Crisp chat', error)
        }
      }
    }
  }
